import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from 'cn';
import { type ReactNode, useRef } from 'react';

type P<TItem> = {
  readonly hasDivider: boolean;
  readonly itemHeight: number;
  readonly items: ReadonlyArray<TItem>;
  readonly renderItem: (item: TItem, index: number) => ReactNode;
} & ComponentStyling;

export const VirtualizedList = <TItem,>({
  className = 'h-96',
  hasDivider = false,
  itemHeight = 48,
  items,
  renderItem,
  style,
}: P<TItem>) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    estimateSize: () => itemHeight,
    getScrollElement: () => parentRef.current,
    overscan: 5,
  });

  return (
    <div
      ref={parentRef}
      className={cn('overflow-y-auto', className)}
      style={style}
    >
      <ul
        className={cn('relative snap-y list-none contain-strict', {
          'divide-y divide-tiga-border-lighter': hasDivider,
        })}
        style={{
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {rowVirtualizer.getVirtualItems().map(({ index, size, start }) => (
          <li
            key={index}
            className={cn('absolute top-0 left-0 w-full', {
              'border-tiga-border-lighter last:border-b!': hasDivider,
            })}
            style={{
              height: `${size}px`,
              transform: `translateY(${start}px)`,
            }}
          >
            {renderItem(items[index], index)}
          </li>
        ))}
      </ul>
    </div>
  );
};
